import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, ShoppingCart, Zap, CheckCircle2, Truck, Banknote } from "lucide-react";
import { toast } from "sonner";
import { http, formatPrice, trackEvent } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { ShareButtons } from "@/components/ShareButtons";
import { useCart } from "@/context/CartContext";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
    trackEvent("product_view", { product_id: id });
    if (new URLSearchParams(window.location.search).get("ref") === "share") {
      trackEvent("share_visit", { product_id: id });
      window.history.replaceState(null, "", window.location.pathname);
    }
    http
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  const images = product ? (product.images?.length ? product.images : [product.image_url]) : [];
  const ratingCount = Number(product?.rating_count) || 0;
  useSeo({
    title: product ? `${product.name} — ${formatPrice(product.price)} | BharatiCrafts` : "",
    description: product
      ? `Buy ${product.name} online at ${formatPrice(product.price)} (${product.discount_pct}% off). ${product.tagline || ""}. Free delivery, Cash on Delivery available.`.replace(/\s+\./g, ".")
      : "",
    jsonLd: product && {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: images.map((u) => (u.startsWith("http") ? u : window.location.origin + u)),
      description: product.description,
      category: product.category,
      sku: product.id,
      brand: { "@type": "Brand", name: "BharatiCrafts" },
      offers: {
        "@type": "Offer",
        url: window.location.href,
        priceCurrency: "INR",
        price: product.price,
        availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
      },
      ...(ratingCount > 0 && {
        aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, ratingCount },
      }),
    },
  });

  if (loading)
    return <div className="py-24 text-center text-[#878787]">Loading…</div>;
  if (!product)
    return <div className="py-24 text-center text-[#878787]">Product not found.</div>;

  const handleAdd = () => {
    addToCart(product);
    toast.success("Added to cart");
  };
  const handleBuy = () => {
    addToCart(product);
    navigate("/cart");
  };
  const handleCod = () => {
    addToCart(product);
    navigate("/cod-checkout");
  };
  const btn = "flex items-center justify-center gap-1.5 text-white font-semibold h-12 rounded-sm hover:brightness-95 transition text-xs sm:text-sm px-1 leading-tight text-center";

  return (
    <main className="max-w-[1400px] mx-auto px-2 sm:px-4 py-3" data-testid="product-detail">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 bg-white rounded-sm fk-shadow p-4 sm:p-6">
        {/* Images + actions */}
        <div className="md:col-span-5">
          <div className="md:sticky md:top-32">
            {(() => {
              const images = product.images?.length ? product.images : [product.image_url];
              const current = images[Math.min(activeImg, images.length - 1)];
              return (
                <>
                  <div className="border border-[#f0f0f0] rounded-sm flex items-center justify-center p-6 h-[320px] sm:h-[420px]">
                    <img
                      src={current}
                      alt={product.name}
                      className="max-h-full max-w-full object-contain mix-blend-multiply"
                      data-testid="product-image-main"
                    />
                  </div>
                  {images.length > 1 && (
                    <div className="flex gap-2 mt-2" data-testid="product-thumbnails">
                      {images.map((img, i) => (
                        <button
                          key={i}
                          onClick={() => setActiveImg(i)}
                          className={`w-16 h-16 border rounded-sm flex items-center justify-center p-1 transition-colors ${
                            i === activeImg ? "border-[#C62828] ring-1 ring-[#C62828]" : "border-[#e0e0e0] hover:border-[#C62828]"
                          }`}
                          data-testid={`product-thumb-${i}`}
                        >
                          <img src={img} alt="" className="max-h-full max-w-full object-contain mix-blend-multiply" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
            <div className="grid grid-cols-3 gap-2 mt-4">
              <button onClick={handleAdd} className={`${btn} bg-[#ff9f00]`} data-testid="add-to-cart-btn">
                <ShoppingCart className="w-4 h-4 shrink-0" /> ADD TO CART
              </button>
              <button onClick={handleBuy} className={`${btn} bg-[#fb641b]`} data-testid="buy-now-btn">
                <Zap className="w-4 h-4 shrink-0" /> BUY NOW
              </button>
              <button onClick={handleCod} className={`${btn} bg-[#C62828]`} data-testid="cod-btn">
                <Banknote className="w-4 h-4 shrink-0" /> COD {formatPrice(Math.round(product.price * 1.12))}
              </button>
            </div>
            <ShareButtons product={product} />
          </div>
        </div>

        {/* Info */}
        <div className="md:col-span-7">
          <p className="text-xs text-[#878787]">{product.category}</p>
          <h1 className="text-lg sm:text-xl text-[#212121] mt-1">{product.name}</h1>

          <div className="flex items-center gap-2 mt-2">
            <span className="flex items-center gap-0.5 bg-[#388e3c] text-white text-xs font-bold px-1.5 py-0.5 rounded-sm">
              {product.rating} <Star className="w-3 h-3 fill-white" />
            </span>
            <span className="text-sm text-[#878787] font-medium">
              {product.rating_count} Ratings
            </span>
          </div>

          <div className="mt-3 flex items-end gap-3 flex-wrap">
            <span className="text-3xl font-semibold text-[#212121]">
              {formatPrice(product.price)}
            </span>
            <span className="text-base text-[#878787] line-through mb-1">
              {formatPrice(product.mrp)}
            </span>
            <span className="text-base font-semibold text-[#388e3c] mb-1">
              {product.discount_pct}% off
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 bg-[#e8f5e9] text-[#388e3c] text-sm font-bold px-3 py-1.5 rounded-sm" data-testid="free-delivery-badge">
              <Truck className="w-4 h-4" /> FREE DELIVERY on all orders
            </span>
            <span className="inline-flex items-center gap-2 bg-[#fff3e0] text-[#e65100] text-sm font-bold px-3 py-1.5 rounded-sm" data-testid="cod-available-badge">
              <Banknote className="w-4 h-4" /> COD Available
            </span>
          </div>

          <div className="mt-5">
            <h3 className="font-head font-bold text-[#212121] mb-2">Highlights</h3>
            <ul className="space-y-1.5">
              {product.specs.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#333]">
                  <CheckCircle2 className="w-4 h-4 text-[#C62828] mt-0.5 shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-5">
            <h3 className="font-head font-bold text-[#212121] mb-2">Description</h3>
            <p className="text-sm text-[#555] leading-relaxed">{product.description}</p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
